
import { supabase } from "@/integrations/supabase/client";
import { graphqlClient } from "@/utils/graphqlClient";
import { store } from '../store/store';
import {
  setSupaCompanies,
  setSupaLoading,
  setSupaError,
  setSupaUniqueCategories,
  setSupaUniqueEmployees
} from '../store/supaCompaniesSlice';
import {
  setPages,
  setLoading,
  setError,
  setUniqueCategories,
  setUniqueEmployees,
  Page
} from '../store/supaPagesSlice';
import { CompanyInput, CompanyOutput, UpdateCompanyParams, SupabaseCompany } from './types/companyTypes';
import { sortCategories, sortEmployeeRanges } from './sorting/sortingUtils';
import { cleanUrl, processSlug, isLegacySlug } from './transforms/slugTransforms';

export const pageSlugToCompanySlug = (pageSlug: string): string | null => {
  const state = store.getState();
  const companies = state.supaCompanies.companies;

  for (const company of companies) {
    for (const page of company.pages) {
      const combinedSlug = company.slug + (page.subslug || '');
      if (combinedSlug === pageSlug) {
        return company.slug;
      }
    }
  }

  return null;
};

export const migrateJson = (input: CompanyInput[]): CompanyOutput[] => {
  console.log('🔄 [companiesHelper] Starting JSON migration');

  // Get current state from Redux stores
  const state = store.getState();
  const storeCompanies = state.supaCompanies.companies;
  const storePages = state.supaPages.pages;

  return input.map(item => {
    try {
      const warnings: string[] = [];
      const [baseSlug, inputSubslug] = processSlug(item.slug || '');

      if (!baseSlug) {
        warnings.push('Missing slug');
        return {
          slug: '',
          logoUrl: null,
          employees: null,
          category: '',
          description: null,
          url: null,
          legacySlug: null,
          pages: [],
          warnings
        };
      }

      // Find matching company and page data from stores
      const storeCompany = storeCompanies.find(company => company.slug === baseSlug);
      const storePage = storePages.find(page => {
        const pageFullSlug = page.slug.startsWith(baseSlug);
        const matchingSubslug = inputSubslug ? page.slug.endsWith(inputSubslug) : true;
        return pageFullSlug && matchingSubslug;
      });

      // Get subslug from store page if available, otherwise use input subslug
      const subslug = storePage?.slug.replace(baseSlug, '') || inputSubslug || '';

      const output: CompanyOutput = {
        slug: baseSlug,
        logoUrl: item.logoUrl || storeCompany?.logoUrl || null,
        employees: item.employees || storeCompany?.employees || storePage?.employees || null,
        category: item.category || storeCompany?.category || storePage?.category || 'Unknown',
        description: item.description || storeCompany?.description || storePage?.description || null,
        url: item.url ? cleanUrl(item.url) : storeCompany?.url || null,
        legacySlug: isLegacySlug(item.slug || '') ? item.slug : storeCompany?.legacySlug || null,
        pages: [{
          subslug,
          pricingPageUrl: item.pricingPageUrl || storePage?.pricingPageUrl || null,
          screenshotUrl: item.screenshotUrl || storePage?.screenshotUrl || null,
          screenshotUrlVersion: item.screenshotUrlVersion || storePage?.screenshotUrlVersion || null
        }]
      };

      if (warnings.length > 0) {
        output.warnings = warnings;
      }

      return output;
    } catch (error) {
      console.error('❌ [companiesHelper] Error processing item:', error);
      return {
        slug: '',
        logoUrl: null,
        employees: null,
        category: '',
        description: null,
        url: null,
        legacySlug: null,
        pages: [],
        warnings: [(error as Error).message || 'Unknown error processing item']
      };
    }
  });
};

export const updateCompany = async ({ slug, employees, category, scraper }: UpdateCompanyParams) => {
  console.log('🔄 [companiesHelper] Updating company:', { slug, employees, category, scraper });

  try {
    // Validate required parameters
    if (!slug) {
      throw new Error('Slug is required');
    }

    if (!employees && !category && scraper === undefined) {
      throw new Error('At least one field (employees, category, or scraper) must be provided');
    }

    // Prepare update data
    const updateData: { employees?: string; category?: string; scraper?: string } = {};
    if (employees) updateData.employees = employees;
    if (category) updateData.category = category;
    if (scraper !== undefined) {
      // Convert "default" to empty string for storage in the database
      updateData.scraper = scraper === "default" ? "" : scraper;
    }

    // Directly update the company in the database
    const { data, error } = await supabase
      .from('companies')
      .update(updateData)
      .eq('slug', slug)
      .select()
      .maybeSingle();

    if (error) {
      console.error('❌ [companiesHelper] Error updating company:', error);
      throw error;
    }

    if (!data) {
      throw new Error(`Company with slug ${slug} not found`);
    }

    // Update companies store
    const state = store.getState();
    const companies = state.supaCompanies.companies.map(company => {
      if (company.slug === slug) {
        return {
          ...company,
          employees: employees || company.employees,
          category: category || company.category,
          scraper: scraper !== undefined ? (scraper === "default" ? "" : scraper) : company.scraper
        };
      }
      return company;
    });

    // Update pages store
    const pages = state.supaPages.pages.map(page => {
      if (page.slug.startsWith(slug)) {
        return {
          ...page,
          employees: employees || page.employees,
          category: category || page.category
        };
      }
      return page;
    });

    // Get updated unique categories and employees
    const unsortedCategories = ['All', ...new Set(companies.map(company => company.category))];
    const unsortedEmployees = ['All', ...new Set(companies.map(company => company.employees))];

    const sortedCategories = sortCategories(unsortedCategories);
    const sortedEmployees = sortEmployeeRanges(unsortedEmployees);

    // Update both stores
    store.dispatch(setSupaCompanies(companies));
    store.dispatch(setSupaUniqueCategories(sortedCategories));
    store.dispatch(setSupaUniqueEmployees(sortedEmployees));

    store.dispatch(setPages(pages));
    store.dispatch(setUniqueCategories(sortedCategories));
    store.dispatch(setUniqueEmployees(sortedEmployees));

    // Format response to match the previous format
    // Use type assertion to avoid TypeScript errors with direct property access
    const dbData = data as any;
    const formattedData = {
      message: 'Company updated successfully',
      slug,
      updates: updateData,
      data: {
        slug: dbData.slug,
        category: dbData.category,
        employees: dbData.employees,
        // Use optional chaining to safely access the scraper field
        scraper: dbData.scraper ?? ''
      }
    };
    
    console.log('✅ [companiesHelper] Company updated successfully:', formattedData);
    return formattedData;
  } catch (error) {
    console.error('❌ [companiesHelper] Error in updateCompany:', error);
    throw error;
  }
};

export const fetchCompaniesBasic = async () => {
  console.log('🔄 [companiesHelper] Checking if basic companies data need to be fetched...');
  const startTime = performance.now();

  // Check if companies are already in Redux store
  const currentState = store.getState();
  if (currentState.supaCompanies.companies.length > 0) {
    console.log('✅ [companiesHelper] Companies already loaded in Redux, skipping fetch');
    return currentState.supaCompanies.companies;
  }

  console.log('🚀 [companiesHelper] USING GRAPHQL - Fetching companies data via GraphQL...');
  store.dispatch(setSupaLoading(true));
  store.dispatch(setSupaError(null));

  try {
    // Basic query with minimal discount data and plans data for add-ons filtering
    const COMPANIES_QUERY = `
      query GetCompanies {
        companies {
          id
          slug
          employees
          category
          logo_url
          description
          url
          pages {
            id
            subslug
            scrapes {
              version
              quarter
              screenshot_url
              verified
            }
            pricing_metrics {
              modality
            }
            discounts {
              type
            }
            plans {
              id
              display_name
              add_on
              plan_name
            }
          }
        }
      }
    `;

    console.log('🔍 [companiesHelper] Executing GraphQL query via graphqlClient');
    console.log('📝 [companiesHelper] Query includes discounts:', COMPANIES_QUERY.includes('discounts'));
    const response = await graphqlClient.request<{ companies: any[] }>(COMPANIES_QUERY);

    console.log('🔍 [companiesHelper] Raw GraphQL response via graphqlClient: Count =', response.companies?.length);
    
    // Debug: Check raw response structure for first company
    if (response.companies?.length > 0) {
      const firstCompany = response.companies[0];
      console.log('🔍 [DEBUG] First company structure:', {
        slug: firstCompany.slug,
        pagesCount: firstCompany.pages?.length || 0,
        firstPage: firstCompany.pages?.[0] ? {
          id: firstCompany.pages[0].id,
          subslug: firstCompany.pages[0].subslug,
          plansCount: firstCompany.pages[0].plans?.length || 0,
          firstPlan: firstCompany.pages[0].plans?.[0] || null,
          discountsFromFirstPlan: firstCompany.pages[0].plans?.[0]?.discounts || null
        } : null
      });
    }

    if (!response || !response.companies || !Array.isArray(response.companies)) {
      console.error('❌ [companiesHelper] Invalid GraphQL response format:', response);
      throw new Error('Invalid response format from GraphQL companies query');
    }

    // Transform GraphQL response to match expected format (with scrapes and pricing metrics)
    const companies = response.companies.map((company: any) => {
      // Extract discounts directly from pages (not from plans!)
      const discounts = company.pages?.flatMap((page: any) => page.discounts || []) || [];
      
      // Debug log for first few companies
      if (company.slug === 'stripe' || company.slug === 'shopify' || company.slug === 'github') {
        console.log(`📦 [Basic Query Transform] ${company.slug}:`, {
          pagesCount: company.pages?.length || 0,
          plansCount: company.pages?.reduce((acc: number, page: any) => acc + (page.plans?.length || 0), 0) || 0,
          discountsCount: discounts.length,
          discountTypes: [...new Set(discounts.map((d: any) => d.type))],
          hasScreenshots: company.pages?.some((page: any) => page.scrapes?.some((scrape: any) => scrape.quarter && scrape.screenshot_url))
        });
      }
      
      return {
        id: company.id,
        slug: company.slug,
        employees: company.employees,
        category: company.category,
        logoUrl: company.logo_url,
        description: company.description,
        url: company.url,
        legacySlug: null, // Add missing property
        tags: [], // No tags in query yet (removed to avoid issues)
        pricingMetrics: company.pages?.flatMap((page: any) => page.pricing_metrics || []) || [],
        discounts,
        pages: company.pages?.map((page: any) => ({
          id: page.id,
          subslug: page.subslug || '',
          screenshotUrl: page.scrapes?.find(s => s.quarter)?.screenshot_url || null,
          screenshotUrlVersion: page.scrapes?.find(s => s.quarter)?.version || null,
          pricingPageUrl: null,
          scrapes: page.scrapes?.map((scrape: any) => ({
            id: scrape.id,
            screenshot_url: scrape.screenshot_url,
            cloudinary_url: scrape.cloudinary_url,
            version: scrape.version,
            quarter: scrape.quarter,
            verified: scrape.verified,
            created_at: scrape.created_at
          })) || [],
          plans: page.plans || []
        })) || []
      };
    });

    // add debug console log to print companies with screenshots
    const companiesWithScreenshots = companies.filter((company: any) => 
      company.pages?.some((page: any) => page.screenshotUrl || (page.scrapes?.length > 0))
    );
    console.log('📸 [companiesHelper] Companies with screenshots:', {
      total: companies.length,
      withScreenshots: companiesWithScreenshots.length,
      sampleWithScreenshot: companiesWithScreenshots[0] || null,
      allCompanySlugs: companies.slice(0, 5).map(c => c.slug)
    });

    // Process companies for the companies store with sorted arrays
    const unsortedCategories = ['All', ...new Set(companies.map((company) => company.category))];
    const unsortedEmployees = ['All', ...new Set(companies.map((company) => company.employees))];

    // Fix the type issues by ensuring arrays contain only strings
    const categories = sortCategories(unsortedCategories.filter(item => typeof item === 'string') as string[]);
    const employees = sortEmployeeRanges(unsortedEmployees.filter(item => typeof item === 'string') as string[]);

    store.dispatch(setSupaCompanies(companies));
    store.dispatch(setSupaUniqueCategories(categories));
    store.dispatch(setSupaUniqueEmployees(employees));

    const endTime = performance.now();
    console.log(`✅ [companiesHelper] Successfully fetched ${companies.length} companies with discount data in ${(endTime - startTime).toFixed(2)}ms`);
    return companies;
  } catch (error) {
    console.error('❌ [companiesHelper] Error in fetchCompaniesBasic:', error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    store.dispatch(setSupaError(errorMessage));
    throw error;
  } finally {
    store.dispatch(setSupaLoading(false));
  }
};

export const fetchCompaniesWithDetails = async () => {
  console.log('🚀 [companiesHelper] USING GRAPHQL - Fetching companies with full details via GraphQL...');
  store.dispatch(setSupaLoading(true));
  store.dispatch(setSupaError(null));
  store.dispatch(setLoading(true));
  store.dispatch(setError(null));

  try {
    const COMPANIES_QUERY = `
      query GetCompanies {
        companies {
          id
          slug
          employees
          category
          logo_url
          description
          url
          pages {
            id
            subslug
            scrapes {
              version
              quarter
              screenshot_url
              verified
            }
            tags {
              name
            }
            plans {
              id
              discounts {
                id
                type
              }
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<{ companies: any[] }>(COMPANIES_QUERY);

    if (!response || !response.companies || !Array.isArray(response.companies)) {
      console.error('❌ [companiesHelper] Invalid GraphQL response format:', response);
      throw new Error('Invalid response format from GraphQL companies query');
    }

    // Transform GraphQL response to match expected format
    const companies = response.companies.map(company => ({
      id: company.id,
      slug: company.slug,
      employees: company.employees,
      category: company.category,
      logoUrl: company.logo_url,
      description: company.description,
      url: company.url,
      tags: company.pages?.flatMap(page => page.tags || []) || [],
      pricingMetrics: [],
      discounts: company.pages?.flatMap(page => 
        page.plans?.flatMap(plan => plan.discounts || []) || []
      ) || [],
      pages: company.pages?.map(page => ({
        id: page.id,
        subslug: page.subslug || '',
        screenshotUrl: page.scrapes?.[0]?.screenshot_url || null,
        screenshotUrlVersion: page.scrapes?.[0]?.version || null,
        pricingPageUrl: null,
        scrapes: (() => {
          // Filter to only quarterly scrapes and deduplicate by quarter
          const quarterMap = new Map();
          page.scrapes?.filter((scrape: any) => scrape.quarter).forEach((scrape: any) => {
            // Keep only the first scrape per quarter (or latest if sorted)
            if (!quarterMap.has(scrape.quarter)) {
              quarterMap.set(scrape.quarter, {
                screenshot_url: scrape.screenshot_url,
                version: scrape.version,
                quarter: scrape.quarter,
                verified: scrape.verified
              });
            }
          });
          return Array.from(quarterMap.values());
        })(),
        plans: page.plans || []
      })) || []
    }));

    // Process companies for the companies store with sorted arrays
    const unsortedCategories = ['All', ...new Set(companies.map((company) => company.category))];
    const unsortedEmployees = ['All', ...new Set(companies.map((company) => company.employees))];

    // Fix the type issues by ensuring arrays contain only strings
    const categories = sortCategories(unsortedCategories.filter(item => typeof item === 'string') as string[]);
    const employees = sortEmployeeRanges(unsortedEmployees.filter(item => typeof item === 'string') as string[]);

    store.dispatch(setSupaCompanies(companies));
    store.dispatch(setSupaUniqueCategories(categories));
    store.dispatch(setSupaUniqueEmployees(employees));

    // Process pages for the pages store
    const pages: Page[] = [];
    companies.forEach(company => {
      company.pages.forEach(page => {
        const combinedSlug = company.slug + (page.subslug || '');
        const legacySlug = company.legacySlug
          ? company.legacySlug + (page.subslug || '')
          : combinedSlug;

        pages.push({
          slug: combinedSlug,
          legacySlug,
          logoUrl: company.logoUrl || '',
          category: company.category,
          employees: company.employees,
          pricingPageUrl: page.pricingPageUrl || '',
          description: company.description || '',
          screenshotUrl: page.screenshotUrl || '',
          screenshotUrlVersion: page.screenshotUrlVersion,
        });
      });
    });

    // Extract and sort unique categories and employees from pages
    const unsortedPageCategories = ['All', ...new Set(pages.map(page => page.category))];
    const unsortedPageEmployees = ['All', ...new Set(pages.map(page => page.employees))];

    // Fix the type issues by ensuring arrays contain only strings
    const pageCategories = sortCategories(unsortedPageCategories.filter(item => typeof item === 'string') as string[]);
    const pageEmployees = sortEmployeeRanges(unsortedPageEmployees.filter(item => typeof item === 'string') as string[]);

    store.dispatch(setPages(pages));
    store.dispatch(setUniqueCategories(pageCategories));
    store.dispatch(setUniqueEmployees(pageEmployees));

    console.log(`✅ [companiesHelper] Successfully fetched ${companies.length} companies and ${pages.length} pages with full details`);
    return companies;
  } catch (error) {
    console.error('❌ [companiesHelper] Error in fetchCompaniesWithDetails:', error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    store.dispatch(setSupaError(errorMessage));
    store.dispatch(setError(errorMessage));
    throw error;
  } finally {
    store.dispatch(setSupaLoading(false));
    store.dispatch(setLoading(false));
  }
};

// Keep the original function for backward compatibility, but use the basic version for better performance
export const fetchCompanies = fetchCompaniesBasic;

// Separate lightweight function to fetch just discount data
export const fetchCompaniesDiscounts = async () => {
  console.log('🎯 [companiesHelper] Fetching discount data separately...');
  
  try {
    // Simplified query - get discounts from pages directly instead of through plans
    const DISCOUNTS_QUERY = `
      query GetCompaniesDiscounts {
        companies {
          slug
          pages {
            discounts {
              id
              type
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<{ companies: any[] }>(DISCOUNTS_QUERY);
    
    if (!response || !response.companies) {
      console.error('❌ [companiesHelper] Invalid discount response format:', response);
      return {};
    }

    // Transform to a lookup map: { companySlug: [discounts] }
    const discountMap: Record<string, any[]> = {};
    
    response.companies.forEach((company: any) => {
      const discounts = company.pages?.flatMap((page: any) => 
        page.discounts || []
      ) || [];
      
      discountMap[company.slug] = discounts;
    });
    
    console.log('✅ [companiesHelper] Successfully fetched discounts for', Object.keys(discountMap).length, 'companies');
    return discountMap;
    
  } catch (error) {
    console.error('❌ [companiesHelper] Error fetching discounts:', error);
    return {};
  }
};

// Separate lightweight function to fetch just plans data for add-ons filtering
export const fetchCompaniesPlans = async () => {
  console.log('📋 [companiesHelper] Fetching plans data for add-ons filtering...');
  
  try {
    // Minimal query just for plans data
    const PLANS_QUERY = `
      query GetCompaniesPlans {
        companies {
          slug
          pages {
            plans {
              id
              display_name
              add_on
              plan_name
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<{ companies: any[] }>(PLANS_QUERY);
    
    if (!response || !response.companies) {
      console.error('❌ [companiesHelper] Invalid plans response format:', response);
      return {};
    }

    // Transform to a lookup map: { companySlug: [plans] }
    const plansMap: Record<string, any[]> = {};
    
    response.companies.forEach((company: any) => {
      const plans = company.pages?.flatMap((page: any) => 
        page.plans || []
      ) || [];
      
      plansMap[company.slug] = plans;
    });
    
    console.log('✅ [companiesHelper] Successfully fetched plans for', Object.keys(plansMap).length, 'companies');
    return plansMap;
    
  } catch (error) {
    console.error('❌ [companiesHelper] Error fetching plans:', error);
    return {};
  }
};

// Function to merge discount data with existing companies in Redux
export const updateCompaniesWithDiscounts = async () => {
  console.log('🎯 [companiesHelper] Updating companies with discount data...');
  
  try {
    const discountMap = await fetchCompaniesDiscounts();
    
    // Get current companies from Redux
    const currentState = store.getState();
    const companies = currentState.supaCompanies.companies;
    
    // Update companies with discount data
    const updatedCompanies = companies.map(company => ({
      ...company,
      discounts: discountMap[company.slug] || []
    }));
    
    // Update Redux store
    store.dispatch(setSupaCompanies(updatedCompanies));
    
    console.log('✅ [companiesHelper] Successfully updated companies with discount data');
    return updatedCompanies;
    
  } catch (error) {
    console.error('❌ [companiesHelper] Error updating companies with discounts:', error);
    return [];
  }
};

// Function to merge plans data with existing companies in Redux
export const updateCompaniesWithPlans = async () => {
  console.log('📋 [companiesHelper] Updating companies with plans data...');
  
  try {
    const plansMap = await fetchCompaniesPlans();
    
    // Get current companies from Redux
    const currentState = store.getState();
    const companies = currentState.supaCompanies.companies;
    
    // Update companies with plans data
    const updatedCompanies = companies.map(company => ({
      ...company,
      pages: company.pages?.map((page: any) => ({
        ...page,
        plans: plansMap[company.slug] || []
      })) || []
    }));
    
    // Update Redux store
    store.dispatch(setSupaCompanies(updatedCompanies));
    
    console.log('✅ [companiesHelper] Successfully updated companies with plans data');
    return updatedCompanies;
    
  } catch (error) {
    console.error('❌ [companiesHelper] Error updating companies with plans:', error);
    return [];
  }
};

// Function to force refresh companies data from the API
export const forceRefreshCompaniesData = async () => {
  console.log('🚀 [companiesHelper] FORCE REFRESH USING GRAPHQL - Force refreshing companies data via GraphQL...');
  store.dispatch(setSupaLoading(true));
  store.dispatch(setSupaError(null));

  try {
    // Ultra-minimal query to avoid 546 timeout errors
    const COMPANIES_QUERY = `
      query GetCompanies {
        companies {
          id
          slug
          employees
          category
          logo_url
          description
          url
          pages {
            id
            subslug
            scrapes {
              version
              quarter
              screenshot_url
              verified
            }
            pricing_metrics {
              modality
            }
          }
        }
      }
    `;

    console.log('🔍 [companiesHelper] FORCE REFRESH - Executing GraphQL query via graphqlClient:', COMPANIES_QUERY);
    const response = await graphqlClient.request<{ companies: any[] }>(COMPANIES_QUERY);

    console.log('🔍 [companiesHelper] FORCE REFRESH - Raw GraphQL response via graphqlClient: Count =', response.companies?.length);

    if (!response || !response.companies || !Array.isArray(response.companies)) {
      console.error('❌ [companiesHelper] Invalid GraphQL response format:', response);
      throw new Error('Invalid response format from GraphQL companies query');
    }

    // Transform GraphQL response to match expected format (with scrapes and pricing metrics)  
    const companies = response.companies.map((company: any) => {
      // Note: Discounts not included in force refresh to avoid timeout - they'll be empty
      const discounts: any[] = [];
      
      // Debug log for first few companies
      if (company.slug === 'stripe' || company.slug === 'shopify' || company.slug === 'github') {
        console.log(`📦 [Force Refresh Transform] ${company.slug}:`, {
          pagesCount: company.pages?.length || 0,
          scrapesCount: company.pages?.reduce((acc: number, page: any) => acc + (page.scrapes?.length || 0), 0) || 0,
          hasScreenshots: company.pages?.some((page: any) => page.scrapes?.some((scrape: any) => scrape.quarter && scrape.screenshot_url))
        });
      }
      
      return {
        id: company.id,
        slug: company.slug,
        employees: company.employees,
        category: company.category,
        logoUrl: company.logo_url,
        description: company.description,
        url: company.url,
        legacySlug: null, // Add missing property
        tags: [], // No tags in query yet (removed to avoid issues)
        pricingMetrics: company.pages?.flatMap((page: any) => page.pricing_metrics || []) || [],
        discounts,
        pages: company.pages?.map((page: any) => ({
          id: page.id,
          subslug: page.subslug || '',
          screenshotUrl: page.scrapes?.find(s => s.quarter)?.screenshot_url || null,
          screenshotUrlVersion: page.scrapes?.find(s => s.quarter)?.version || null,
          pricingPageUrl: null,
          scrapes: page.scrapes?.map((scrape: any) => ({
            id: scrape.id,
            screenshot_url: scrape.screenshot_url,
            cloudinary_url: scrape.cloudinary_url,
            version: scrape.version,
            quarter: scrape.quarter,
            verified: scrape.verified,
            created_at: scrape.created_at
          })) || [],
          plans: [] // No plans in basic query to avoid timeout
        })) || []
      };
    });

    // Process companies for the companies store with sorted arrays
    const unsortedCategories = ['All', ...new Set(companies.map((company) => company.category))];
    const unsortedEmployees = ['All', ...new Set(companies.map((company) => company.employees))];

    // Fix the type issues by ensuring arrays contain only strings
    const categories = sortCategories(unsortedCategories.filter(item => typeof item === 'string') as string[]);
    const employees = sortEmployeeRanges(unsortedEmployees.filter(item => typeof item === 'string') as string[]);

    store.dispatch(setSupaCompanies(companies));
    store.dispatch(setSupaUniqueCategories(categories));
    store.dispatch(setSupaUniqueEmployees(employees));

    console.log(`✅ [companiesHelper] Successfully force refreshed ${companies.length} companies`);
    
    // Load discount data separately in the background
    updateCompaniesWithDiscounts().catch(error => {
      console.warn('⚠️ [companiesHelper] Failed to load discount data after refresh:', error);
    });
    
    return companies;
  } catch (error) {
    console.error('❌ [companiesHelper] Error in forceRefreshCompaniesData:', error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    store.dispatch(setSupaError(errorMessage));
    throw error;
  } finally {
    store.dispatch(setSupaLoading(false));
  }
};

// Add a function to check if we have detailed data loaded
export const hasDetailedData = (): boolean => {
  const state = store.getState();
  if (state.supaCompanies.companies.length === 0) {
    return false;
  }

  // Check if at least one company has pricing metrics
  return state.supaCompanies.companies.some(company =>
    company.pricingMetrics && company.pricingMetrics.length > 0
  );
};

export const getScreenshotUrl = (slug: string): string | undefined => {
  const state = store.getState();
  const pages = state.supaPages.pages;
  const matchingPage = pages.find(page => page.slug === slug);
  return matchingPage?.screenshotUrl;
};

export const getCategory = (pageSlug: string): string | undefined => {
  const companySlug = pageSlugToCompanySlug(pageSlug);
  const state = store.getState();
  const companies = state.supaCompanies.companies;
  const matchingPage = companies.find(company => company.slug === companySlug);
  return matchingPage?.category;
};

export const getSlugsByMetricName = async (metricName: string): Promise<string[]> => {
  try {
    // Use a direct SQL query with the from() method instead of a view
    const { data } = await supabase
      .from('pricing_metrics')
      .select('name, page_pricing_metrics(slug)')
      .eq('name', metricName);

    if (!data || data.length === 0) {
      return [];
    }

    // Extract the slugs from the nested data
    const slugs: string[] = [];
    data.forEach(item => {
      if (item.page_pricing_metrics && Array.isArray(item.page_pricing_metrics)) {
        item.page_pricing_metrics.forEach((metric: any) => {
          if (metric.slug) slugs.push(metric.slug);
        });
      }
    });

    return slugs;
  } catch (error) {
    console.error('Error fetching slugs by metric name:', error);
    return [];
  }
};

export const getSlugsByMetricExample = async (example: string): Promise<string[]> => {
  try {
    const { data } = await supabase
      .from('page_pricing_metrics')
      .select('slug')
      .ilike('sample_values', `%${example}%`)
      .order('slug');

    if (!data || data.length === 0) {
      return [];
    }

    // Map the slugs from the response
    return data.map(item => item.slug);
  } catch (error) {
    console.error('Error fetching slugs by metric example:', error);
    return [];
  }
};
